/**
 * PAGE ADMIN — Automatisations BED (migration 00233)
 *
 * Cahier Desertours L30-33 / L35-51 / L53-54 / L59-76 / L93-94 / L123 / L137-151.
 *
 * Trois blocs, pas un de plus :
 *   1. les RÈGLES (événements + relances), réglables inline ;
 *   2. la PRÉVISUALISATION des relances dues, avec le verdict de l'anti-spam
 *      cible par cible — l'admin doit distinguer « sélectionné » de « partira » ;
 *   3. la CLÔTURE d'édition.
 *
 * Le contenu des mails, lui, n'est PAS ici : ce sont des modèles ordinaires de
 * « Modèles d'email », éditables au même endroit que tous les autres.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Play, TestTube2 } from 'lucide-react';
import { PageContent, PageHeader, PageLayout } from '../components/page';
import { BedAutomationRuleRow } from '../components/bed/BedAutomationRuleRow';
import { BedEditionClosureCard } from '../components/bed/BedEditionClosureCard';
import { BedOpeningCard } from '../components/bed/BedOpeningCard';
import { Badge, Button, ConfirmDialog, ErrorState, LoadingState, toast } from '../components/ui';
import { useEmailTemplates } from '../hooks/useEmailTemplates';
import { usePermissions } from '../hooks/usePermissions';
import { ROUTES } from '../lib/routes';
import {
  bedAutomationErrorMessage,
  gateReasonLabel,
  getBedAutomationRules,
  getBedReminderPreview,
  isTargetRuleActive,
  recipientsOfRule,
  runBedAutomations,
  setBedAutomationRule,
  type BedAutomationRules,
  type BedReminderTarget,
  type BedRulePatch,
} from '../lib/bed/automations';

export function BedAutomationsPage() {
  const { templates } = useEmailTemplates();
  const { role } = usePermissions();
  const [data, setData] = useState<BedAutomationRules | null>(null);
  const [targets, setTargets] = useState<BedReminderTarget[]>([]);
  // Aperçu en échec : on ne sait pas à qui une relance partirait, donc on
  // n'en active aucune (sinon elle s'activait sans confirmation).
  const [previewFailed, setPreviewFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 00297 — activation d'une relance qui a déjà des destinataires : on les
  // montre et on demande confirmation avant d'activer.
  const [pendingActivation, setPendingActivation] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [rules, preview] = await Promise.all([getBedAutomationRules(), getBedReminderPreview()]);

    if (rules.status !== 'ok') {
      setError(bedAutomationErrorMessage(rules.status));
      setLoading(false);
      return;
    }
    setError(null);
    setData(rules.data);
    setTargets(preview.status === 'ok' ? preview.data : []);
    setPreviewFailed(preview.status !== 'ok');
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handlePatch(key: string, patch: BedRulePatch) {
    setBusy(true);
    const before = data?.rules.find((r) => r.key === key);
    const res = await setBedAutomationRule(key, patch);
    if (res.status === 'ok') {
      setData(res.data);
      // 00297 — désactiver une règle annule ses mails en attente : on le dit.
      const after = res.data.rules.find((r) => r.key === key);
      const cancelled =
        patch.is_active === false
          ? Math.max((before?.pending_count ?? 0) - (after?.pending_count ?? 0), 0)
          : 0;
      toast.success(
        cancelled > 0
          ? `Règle désactivée : ${cancelled} mail(s) en attente annulé(s)`
          : 'Règle mise à jour'
      );
      // Le nombre de cibles dues dépend du délai et de l'activation : il faut
      // le recalculer, pas seulement rafraîchir la règle.
      const preview = await getBedReminderPreview();
      setTargets(preview.status === 'ok' ? preview.data : []);
      setPreviewFailed(preview.status !== 'ok');
    } else {
      toast.error(bedAutomationErrorMessage(res.status));
    }
    setBusy(false);
  }

  /** Activer une relance qui partirait à quelqu'un passe par une confirmation. */
  function requestPatch(key: string, patch: BedRulePatch) {
    const rule = data?.rules.find((r) => r.key === key);
    if (patch.is_active === true && rule?.kind === 'reminder' && previewFailed) {
      toast.error(
        'Activation impossible : la liste des destinataires n’a pas pu être chargée. Rechargez la page.'
      );
      return;
    }
    if (
      patch.is_active === true &&
      rule?.kind === 'reminder' &&
      recipientsOfRule(targets, key).length > 0
    ) {
      setPendingActivation(key);
      return;
    }
    void handlePatch(key, patch);
  }

  async function handleRun(mode: 'run' | 'preview') {
    setBusy(true);
    const res = await runBedAutomations(mode);
    if (res.status === 'ok') {
      const { reminders, queue } = res.data;
      // Recette du 07/10 : « 0 envoyé, 0 échec » masquait les envois bloqués
      // (liste autorisée du staging, modèle absent…) et la file restante.
      toast.success(
        mode === 'preview'
          ? `Simulation : ${reminders?.selected ?? 0} cible(s), ${queue.processed} mail(s) rendus, 0 envoyé`
          : `${reminders?.enqueued ?? 0} relance(s) mise(s) en file · ${queue.sent} envoyé(s), ${queue.skipped} non envoyé(s), ${queue.failed} en échec` +
              ((queue.retried ?? 0) > 0 ? `, ${queue.retried} à réessayer` : ''),
        mode === 'run' && queue.processed >= 25
          ? {
              description:
                'Un passage traite 25 emails au plus : d’autres peuvent rester en file, cliquez à nouveau.',
            }
          : undefined
      );
      await refresh();
    } else {
      toast.error(res.message ?? "L'exécution a échoué.");
    }
    setBusy(false);
  }

  if (loading) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Admin' }, { label: 'Automatisations BED' }]} />
        <div className="h-px w-full bg-zinc-200" />
        <LoadingState message="Chargement des automatisations..." />
      </PageLayout>
    );
  }

  if (error || !data) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Admin' }, { label: 'Automatisations BED' }]} />
        <div className="h-px w-full bg-zinc-200" />
        <ErrorState
          title="Automatisations BED indisponibles"
          message={error ?? 'Une erreur est survenue.'}
          onRetry={refresh}
        />
      </PageLayout>
    );
  }

  const events = data.rules.filter((r) => r.kind === 'event');
  const reminders = data.rules.filter((r) => r.kind === 'reminder');
  const activeTargets = targets.filter(isTargetRuleActive);
  const inactiveTargets = targets.filter((t) => !isTargetRuleActive(t));
  const willSend = activeTargets.filter((t) => t.gate.allowed).length;
  const activationRule = data.rules.find((r) => r.key === pendingActivation) ?? null;
  const activationRecipients = pendingActivation
    ? recipientsOfRule(targets, pendingActivation)
    : [];

  return (
    <PageLayout>
      <PageHeader breadcrumbs={[{ label: 'Admin' }, { label: 'Automatisations BED' }]} />
      <div className="h-px w-full bg-zinc-200" />

      <PageContent
        title="Automatisations BED"
        description="Emails automatiques, relances programmables et clôture d'édition du bulletin d'engagement."
        actions={
          <>
            <Button variant="outline" disabled={busy} onClick={() => handleRun('preview')}>
              <TestTube2 className="size-4" aria-hidden />
              Simuler
            </Button>
            <Button disabled={busy} onClick={() => handleRun('run')}>
              <Play className="size-4" aria-hidden />
              Exécuter maintenant
            </Button>
          </>
        }
      >
        <div className="flex flex-col divide-y divide-zinc-200">
          <section className="flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-2 px-page-padding pt-6 pb-2">
              <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold text-zinc-900">Emails événementiels</h2>
                <p className="text-sm text-zinc-500">
                  Mis en file au moment où le fait se produit (annulation ou report confirmé,
                  inscription définitive, encaissement du solde), puis envoyés au passage suivant
                  des automatisations : « Exécuter maintenant » tant que l&apos;exécution
                  automatique n&apos;est pas branchée.
                </p>
              </div>
              {/* La page des modèles est réservée au superadmin : un admin qui
                  cliquait était renvoyé à l'accueil sans message (TICKET-BED-30). */}
              {role === 'superadmin' ? (
                <Link
                  to={ROUTES.ADMIN.EMAIL_TEMPLATES}
                  className="flex items-center gap-1.5 text-sm text-zinc-600 underline underline-offset-4 hover:text-zinc-900"
                >
                  Éditer les modèles d'email
                  <ExternalLink className="size-3.5" aria-hidden />
                </Link>
              ) : (
                <span className="text-sm text-zinc-500">
                  Modèles d&apos;email modifiables par un superadmin
                </span>
              )}
            </div>
            {events.map((rule) => (
              <BedAutomationRuleRow
                key={rule.key}
                rule={rule}
                templates={templates}
                dueNow={0}
                disabled={busy}
                onPatch={requestPatch}
              />
            ))}
          </section>

          <section className="flex flex-col">
            <div className="flex flex-col gap-1 px-page-padding pt-6 pb-2">
              <h2 className="text-base font-semibold text-zinc-900">Relances programmées</h2>
              <p className="text-sm text-zinc-500">
                Évaluées à chaque passage des automatisations : chaque jour à 07h30 UTC quand
                l&apos;exécution automatique est branchée, sinon au clic « Exécuter maintenant ».
                Les équipages annulés, reportés, inscrits définitifs ou archivés en sont exclus
                d&apos;office.
              </p>
            </div>
            {reminders.map((rule) => (
              <BedAutomationRuleRow
                key={rule.key}
                rule={rule}
                templates={templates}
                dueNow={data.due_now[rule.key] ?? 0}
                recipientsIfActivated={recipientsOfRule(targets, rule.key).length}
                disabled={busy}
                onPatch={requestPatch}
              />
            ))}
          </section>

          <section className="flex flex-col gap-3 px-page-padding py-6">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-base font-semibold text-zinc-900">Cibles du prochain passage</h2>
              <Badge variant="secondary">{activeTargets.length} sélectionnée(s)</Badge>
              <Badge variant="outline">{willSend} partiraient réellement</Badge>
            </div>

            {previewFailed ? (
              <p className="text-sm text-red-700" role="alert">
                Aperçu indisponible : impossible de savoir à qui partiraient les relances. Les
                relances désactivées ne peuvent pas être activées tant que la page n’a pas été
                rechargée.
              </p>
            ) : activeTargets.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Aucun équipage ne remplit les conditions d'une relance active aujourd'hui.
              </p>
            ) : (
              <TargetsTable targets={activeTargets} />
            )}
          </section>

          {inactiveTargets.length > 0 && (
            <section
              className="flex flex-col gap-3 px-page-padding py-6"
              data-testid="bed-inactive-targets"
            >
              <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold text-zinc-900">
                  Relances désactivées : à qui elles partiraient
                </h2>
                <p className="text-sm text-zinc-500">
                  Rien ne part tant que la relance est désactivée. Voici les équipages et les
                  adresses concernés si vous l’activez aujourd’hui.
                </p>
              </div>
              <TargetsTable targets={inactiveTargets} />
            </section>
          )}

          <BedOpeningCard />

          <BedEditionClosureCard />
        </div>
      </PageContent>
      <ConfirmDialog
        open={pendingActivation !== null}
        onOpenChange={(open) => {
          if (!open) setPendingActivation(null);
        }}
        title={`Activer « ${activationRule?.label ?? ''} » ?`}
        description={`Au prochain passage (chaque jour à 07h30 UTC, ou « Exécuter maintenant »), cette relance partira à ${activationRecipients.length} équipage(s) : ${activationRecipients
          .slice(0, 8)
          .map(
            (t) =>
              `${t.crew_number ?? t.crew_name ?? 'équipage'} (${t.recipient_email ?? 'sans adresse'})`
          )
          .join(
            ', '
          )}${activationRecipients.length > 8 ? `, et ${activationRecipients.length - 8} autre(s)` : ''}. La liste complète est en bas de page.`}
        confirmLabel="Activer la relance"
        onConfirm={() => {
          const key = pendingActivation;
          setPendingActivation(null);
          if (key) void handlePatch(key, { is_active: true });
        }}
      />
    </PageLayout>
  );
}

/** Tableau des cibles d'une relance : qui, à quelle adresse, pourquoi, et si le mail partira. */
function TargetsTable({ targets }: { targets: BedReminderTarget[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[60rem] text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-left text-xs tracking-wide text-zinc-500 uppercase">
            <th className="py-2 pr-4 font-medium">Équipage</th>
            <th className="py-2 pr-4 font-medium">Destinataire</th>
            <th className="py-2 pr-4 font-medium">Statut BED</th>
            <th className="py-2 pr-4 font-medium">Relance</th>
            <th className="py-2 pr-4 font-medium">Motif</th>
            <th className="py-2 font-medium">Anti-spam</th>
          </tr>
        </thead>
        <tbody>
          {targets.map((t) => (
            <tr
              key={`${t.rule_key}:${t.crew_id}`}
              className="border-b border-zinc-100 last:border-b-0"
            >
              <td className="py-2 pr-4">
                <Link
                  to={ROUTES.DATA.CREWS.DETAIL(t.crew_id)}
                  className="text-zinc-900 underline underline-offset-4"
                >
                  {t.crew_number ?? t.crew_id.slice(0, 8)}
                </Link>
                <span className="ml-2 text-zinc-500">{t.crew_name}</span>
              </td>
              <td className="py-2 pr-4 text-zinc-600">
                {t.recipient_email ?? <span className="text-red-700">Aucune adresse</span>}
              </td>
              <td className="py-2 pr-4 text-zinc-600">{t.bed_status_label}</td>
              <td className="py-2 pr-4 text-zinc-600">{t.rule_label ?? t.rule_key}</td>
              <td className="py-2 pr-4 text-zinc-600">{t.detail}</td>
              <td className="py-2">
                <Badge variant={t.gate.allowed ? 'secondary' : 'outline'}>
                  {gateReasonLabel(t.gate, isTargetRuleActive(t))}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
